import { forwardRef } from "react";
import {
  Chip as MuiChip,
  Stack,
  type ChipProps as MuiChipProps,
} from "@mui/material";

import { Icon } from "../Icon";
import { tokens } from "../../tokens/generated/tokens";

import type { SxProps, Theme } from "@mui/material/styles";
import type { ReactNode } from "react";

const { display, focus } = tokens.theme.light;

/** Design names the informational tone `accent`; the prop keeps `info`. */
const DISPLAY_GROUP: Record<ChipStatus, keyof typeof display> = {
  default: "default",
  success: "success",
  severe: "severe",
  danger: "danger",
  info: "accent",
  attention: "attention",
};
const { borderRadius, borderWidth, space } = tokens.functional.size;
const { control } = tokens.pattern.size;
const { text } = tokens.base.typography;
const { negative6 } = tokens.base.size;

export type ChipStatus =
  | "default"
  | "success"
  | "severe"
  | "danger"
  | "info"
  | "attention";
export type ChipVisibility = "outlined" | "subtle" | "bold";
export type ChipSize = "small" | "medium";

/**
 * `action` and `onDelete` rise and fall together: MUI only renders a delete
 * icon when `onDelete` is set, so an `action` prop with no handler would
 * silently render no affordance, and an `onDelete` with no `action` would
 * silently never be called.
 */
type ChipActionProps =
  | { action?: false; onDelete?: undefined }
  | {
      /** Shows the 20x20 delete affordance at 50% opacity. */
      action: true;
      /** Delete handler, fired by the action affordance. */
      onDelete: MuiChipProps["onDelete"];
    };

export type ChipProps = Omit<
  MuiChipProps,
  | "variant"
  | "size"
  | "color"
  | "icon"
  | "avatar"
  | "onDelete"
  | "deleteIcon"
  | "action"
> & {
  /** Status axis, replacing MUI `color`. Default: 'default'. */
  color?: ChipStatus;
  /** Visibility axis, replacing MUI `variant`. Default: 'outlined'. */
  variant?: ChipVisibility;
  /** Chip size. Default: 'medium'. */
  size?: ChipSize;
  /**
   * Leading 16x16 visual slot. `true` renders a default placeholder glyph, a
   * node renders in its place, `false` hides the slot. Default: true.
   */
  leadingVisual?: boolean | ReactNode;
  /**
   * Trailing 16x16 visual slot, same contract as `leadingVisual`.
   * Default: false.
   */
  trailingVisual?: boolean | ReactNode;
  /** Root test id. Default: "ds-chip". */
  "data-testid"?: string;
} & ChipActionProps;

const sizeMap: Record<ChipSize, MuiChipProps["size"]> = {
  small: "small",
  medium: "medium",
};

const defaultVisual = <Icon name="highlight" size="sm" color="inherit" />;

function resolveVisual(visual: boolean | ReactNode | undefined): ReactNode {
  if (visual === false || visual === undefined) return null;
  if (visual === true) return defaultVisual;
  return visual;
}

const visualSlotSx: SxProps<Theme> = (theme) => ({
  display: "inline-flex",
  width: theme.typography.pxToRem(16),
  height: theme.typography.pxToRem(16),
  "& svg": { fontSize: theme.typography.pxToRem(16) },
});

/**
 * The medium Chip draws its inline padding from the small control scale, and
 * the small Chip from xsmall — Figma's own binding, not a typo.
 */
const dimensionsBySize: Record<
  ChipSize,
  {
    paddingInline: number;
    paddingBlock: number;
    fontSize: number;
    lineHeight: number;
    letterSpacing: number;
  }
> = {
  medium: {
    paddingInline: control.small.paddingInline.normal,
    paddingBlock: control.medium.paddingBlock,
    fontSize: text.body.fontSize.medium,
    lineHeight: text.body.lineHeight.medium,
    letterSpacing: text.body.letterSpacing.medium,
  },
  small: {
    paddingInline: control.xsmall.paddingInline.normal,
    paddingBlock: control.xsmall.paddingBlock,
    fontSize: text.body.fontSize.small,
    lineHeight: text.body.lineHeight.small,
    letterSpacing: text.body.letterSpacing.small,
  },
};

/** Per-size padding, gap, and typography, independent of status/visibility. */
function buildSizeSx(size: ChipSize): SxProps<Theme> {
  const { paddingInline, paddingBlock, fontSize, lineHeight, letterSpacing } =
    dimensionsBySize[size];

  return (theme) => ({
    height: "auto",
    gap: theme.typography.pxToRem(control.xsmall.gap),
    paddingInline: theme.typography.pxToRem(paddingInline),
    paddingBlock: theme.typography.pxToRem(paddingBlock),
    borderRadius: theme.typography.pxToRem(borderRadius.full),
    fontFamily: "inherit",
    fontSize: theme.typography.pxToRem(fontSize),
    lineHeight,
    fontWeight: text.body.fontWeight,
    letterSpacing,
    "& .MuiChip-label": { padding: 0 },
    "& .MuiChip-icon": { margin: 0 },
    "& .MuiChip-deleteIcon": {
      display: "inline-flex",
      margin: 0,
      marginInlineEnd: theme.typography.pxToRem(negative6),
      width: theme.typography.pxToRem(20),
      height: theme.typography.pxToRem(20),
      opacity: 0.5,
      "& svg": { fontSize: theme.typography.pxToRem(20) },
    },
  });
}

type DisplayTone = (typeof display)["default"]["outlined"];

/**
 * One mode's surface, border, text, and icon colors across every state.
 * Every `display/*` style carries a `borderColor` token, including `subtle`
 * (transparent) and `bold` (matches the fill) — applying it unconditionally
 * renders correctly for all three and keeps this in lockstep with the
 * generated token shape instead of re-deriving which styles "have" a border.
 */
function buildToneSx(
  theme: Theme,
  tone: DisplayTone,
  outlineColor: string,
): Record<string, unknown> {
  return {
    color: tone.fgColor.rest,
    backgroundColor: tone.bgColor.rest,
    border: `${theme.typography.pxToRem(borderWidth.default)} solid ${tone.borderColor.rest}`,
    "& .MuiChip-icon, & .MuiChip-deleteIcon": { color: tone.iconColor.rest },
    "&:hover:not(.Mui-disabled)": {
      color: tone.fgColor.hover,
      backgroundColor: tone.bgColor.hover,
      borderColor: tone.borderColor.hover,
      "& .MuiChip-icon, & .MuiChip-deleteIcon": { color: tone.iconColor.hover },
    },
    "&:active:not(.Mui-disabled)": {
      color: tone.fgColor.active,
      backgroundColor: tone.bgColor.active,
      borderColor: tone.borderColor.active,
      "& .MuiChip-icon, & .MuiChip-deleteIcon": {
        color: tone.iconColor.active,
      },
    },
    "&.Mui-disabled": {
      opacity: 1,
      color: tone.fgColor.disabled,
      backgroundColor: tone.bgColor.disabled,
      borderColor: tone.borderColor.disabled,
      "& .MuiChip-icon, & .MuiChip-deleteIcon": {
        color: tone.iconColor.disabled,
      },
    },
    "&.Mui-focusVisible": {
      color: tone.fgColor.rest,
      backgroundColor: tone.bgColor.rest,
      outline: `${theme.typography.pxToRem(borderWidth.thick)} solid ${outlineColor}`,
      outlineOffset: theme.typography.pxToRem(space.xxsmall),
    },
  };
}

/**
 * Per-status/visibility colors for both color schemes. The light values apply
 * directly; the dark values are nested under the selector `cssVariables`
 * themes (e.g. `kaizenTheme`) toggle via a `data-mui-color-scheme` attribute
 * — see `SectionHeader`'s `sectionHeaderContainer` for the same pattern. A
 * theme without `cssVariables` never renders that attribute, so it keeps the
 * light values unconditionally, same as before.
 */
function buildStatusSx(
  status: ChipStatus,
  visibility: ChipVisibility,
): SxProps<Theme> {
  const group = DISPLAY_GROUP[status];
  const tone = display[group][visibility];
  const darkTone = tokens.theme.dark.display[group][visibility];
  const darkOutlineColor = tokens.theme.dark.focus.outlineColor;

  return (theme) => ({
    ...buildToneSx(theme, tone, focus.outlineColor),
    '[data-mui-color-scheme="dark"] &': buildToneSx(
      theme,
      darkTone,
      darkOutlineColor,
    ),
  });
}

/**
 * Centro/Kaizen wrapper over MUI's Chip: a Status and a Visibility axis bound
 * to `display/*` tokens, replacing MUI's `color` and `variant`.
 */
export const Chip = forwardRef<HTMLDivElement, ChipProps>((props, ref) => {
  const {
    color = "default",
    variant = "outlined",
    size = "medium",
    leadingVisual = true,
    trailingVisual = false,
    action = false,
    onDelete,
    label,
    sx,
    "data-testid": dataTestId = "ds-chip",
    ...rest
  } = props;

  const resolvedLeadingVisual = resolveVisual(leadingVisual);
  const resolvedTrailingVisual = resolveVisual(trailingVisual);

  return (
    <MuiChip
      ref={ref}
      data-testid={dataTestId}
      size={sizeMap[size]}
      icon={
        resolvedLeadingVisual !== null ? (
          <Stack sx={visualSlotSx}>{resolvedLeadingVisual}</Stack>
        ) : undefined
      }
      label={
        resolvedTrailingVisual ? (
          <Stack
            direction="row"
            alignItems="center"
            sx={(theme) => ({
              gap: theme.typography.pxToRem(control.xsmall.gap),
            })}
          >
            <span>{label}</span>
            <Stack sx={visualSlotSx}>{resolvedTrailingVisual}</Stack>
          </Stack>
        ) : (
          label
        )
      }
      deleteIcon={
        action ? (
          <Stack>
            <Icon name="close" size="sm" color="inherit" />
          </Stack>
        ) : undefined
      }
      onDelete={action ? onDelete : undefined}
      sx={
        [buildSizeSx(size), buildStatusSx(color, variant), sx] as SxProps<Theme>
      }
      {...rest}
    />
  );
});

Chip.displayName = "Chip";
